import React from 'react';
import * as _ from 'lodash';
import c from 'classnames';
import { Checkbox } from '@miotech/mprod';

import Pagination from '../Pagination/Pagination';
import './Table.global.styl';

const DEFAULT_COLUMN_WIDTH = '100px';
const CHECKBOX_COLUMN_WIDTH = '60px';

const ROW_HEIGHT = '32px';
const HEADER_ROW_HEIGHT = '32px';

type SortDirection = 'ASC' | 'DESC' | undefined;
interface SortState {
  sortDirection?: SortDirection;
  sortColumn?: string;
}
type GroupByOption =
  | string
  | {
      field: string;
      formatter: (group: any) => string;
    };

interface GroupInfo extends SortState {
  type?: string;
  name?: string;
  entry?: any;
  itemType?: string;
  value?: string;
  entryCount?: number;
  path?: string[];
  data?: GroupInfo[];
  expanded?: boolean;
  currentPage?: number;
  pagedData?: any[];
  sortedData?: any[];
}

const SortingIcon = ({ direction }: { direction?: SortDirection }) => (
  <div className="mio-table__sort-icon">
    <i
      className={`iconfont icon-sort-up up-icon ${
        direction === 'ASC' ? 'active' : ''
      }`}
    />
    <i
      className={`iconfont icon-sort-down down-icon ${
        direction === 'DESC' ? 'active' : ''
      }`}
    />
  </div>
);

SortingIcon.defaultProps = {
  direction: undefined,
};

function defaultGroupByFormatter(groupInfo: GroupInfo) {
  return `${groupInfo.value} (${groupInfo.entryCount})`;
}

function getDataFromEntry(
  accessor:
    | string
    | ((entry: any, index: number, data?: GroupInfo[]) => React.ReactNode),
  entry: any,
  index: number,
  data?: GroupInfo[],
) {
  if (typeof accessor === 'string') {
    return entry[accessor];
  } else if (typeof accessor === 'function') {
    return accessor(entry, index, data);
  }
  return '';
}

function formGroup(
  data: any[],
  groupBy: GroupByOption[],
  pageSize: number,
  path: string[],
) {
  if (groupBy.length === 0) {
    return data.map(d => ({
      type: 'entry',
      entry: d,
    }));
  }

  let [groupByField] = groupBy;
  let groupByFormatter = defaultGroupByFormatter;

  if (typeof groupBy[0] === 'object') {
    groupByField = _.get(groupBy[0], 'field', '');
    if (Boolean(groupBy[0].formatter)) {
      groupByFormatter = groupBy[0].formatter;
    }
  }

  const group = _.groupBy(data, groupByField);
  const result: GroupInfo[] = [];
  _.each(group, (groupData, groupKey) => {
    const currentPath = [...path, groupKey];
    const groupInfo: GroupInfo = {
      type: 'group',
      itemType: groupBy.length === 1 ? 'entry' : 'group',
      value: groupKey,
      entryCount: groupData.length,
      path: currentPath,
      data: formGroup(groupData, groupBy.slice(1), pageSize, currentPath),
      expanded: true,
    };
    groupInfo.name = groupByFormatter(groupInfo);

    if (pageSize) {
      groupInfo.currentPage = 1;
      groupInfo.pagedData = (groupInfo.data || []).slice(0, pageSize);
    }

    result.push(groupInfo);
  });
  return result;
}

function generateHierarchicalDataFromArray(
  data: any[],
  groupBy: GroupByOption[],
  pageSize: number,
) {
  const result: GroupInfo = {
    itemType: groupBy.length === 0 ? 'entry' : 'group',
    data: formGroup(data, groupBy, pageSize, []),
  };

  // TODO remove duplicate code with function formGroup()
  if (pageSize) {
    result.currentPage = 1;
    result.pagedData = (result.data || []).slice(0, pageSize);
  }

  return result;
}

type Component<P> =
  | React.ComponentType<P>
  | React.ForwardRefExoticComponent<P>
  | React.FC<P>
  | keyof React.ReactHTML;

export type CustomizeComponent = Component<any>;

export interface TableProps {
  tableId: string;

  data: any[];
  columns: any[];
  id: string;
  groupBy: GroupByOption[];

  showRowSelection: boolean;
  selectedRowIds: string[];
  onSelectedRowsChange: (
    selectedRowIds: string[],
    selectedRows: any[],
  ) => void | Promise<void>;
  onRowCheckedStatusChange: (
    id: string,
    checked: boolean,
  ) => void | Promise<void>;

  height: string;
  headerHeight: string;

  pagination: 'front-end' | 'back-end';
  pageSize: number; // for front-end pagination
  pageCount: number; // for back-end pagination
  currentPage: number; // for back-end pagination
  updateDataForPage: (page: number) => void | Promise<void>; // for back-end pagination
  paginationWithSearch: boolean;

  rowElement: CustomizeComponent;
  rowProps: Record<string, any>;
  rowDetailRender: (entry: any) => React.ReactNode;

  updateSort: (sortState: SortState) => void | Promise<void>;
}

interface State {
  selectedRowMap: Record<string, boolean>;
  hierarchicalData: GroupInfo;
}

class Table extends React.Component<TableProps, State> {
  static defaultProps = {
    tableId: undefined,

    data: [],
    columns: [],
    id: 'id',
    groupBy: [],

    showRowSelection: false,
    selectedRowIds: [],
    onSelectedRowsChange: () => {},
    onRowCheckedStatusChange: () => {},

    height: undefined,
    headerHeight: HEADER_ROW_HEIGHT,

    pagination: undefined,
    pageSize: undefined,
    pageCount: undefined,
    currentPage: undefined,
    updateDataForPage: () => {},
    paginationWithSearch: false,

    rowElement: 'div',
    rowProps: {},
    rowDetailRender: () => {},

    updateSort: () => {},
  } as Partial<TableProps>;

  constructor(props: TableProps) {
    super(props);

    const selectedRowMap: Record<string, boolean> = {};
    props.selectedRowIds.forEach(id => {
      selectedRowMap[id] = true;
    });

    const hierarchicalData = generateHierarchicalDataFromArray(
      props.data,
      props.groupBy,
      props.pageSize,
    );

    this.state = {
      selectedRowMap,
      hierarchicalData,
    };
  }

  componentWillReceiveProps(nextProps: TableProps) {
    if (nextProps.selectedRowIds !== this.props.selectedRowIds) {
      const selectedRowMap: Record<string, boolean> = {};
      nextProps.selectedRowIds.forEach(id => {
        selectedRowMap[id] = true;
      });

      this.setState({ selectedRowMap });
    }

    if (
      nextProps.groupBy !== this.props.groupBy ||
      nextProps.data !== this.props.data ||
      nextProps.pageSize !== this.props.pageSize
    ) {
      this.setState({
        hierarchicalData: generateHierarchicalDataFromArray(
          nextProps.data,
          nextProps.groupBy,
          nextProps.pageSize,
        ),
      });
    }
  }

  onCheckboxChange(id: string, checked: boolean) {
    this.setState(
      {
        selectedRowMap: {
          ...this.state.selectedRowMap,
          [id]: checked,
        },
      },
      () => {
        const selectedRows = this.props.data.filter(
          entry => this.state.selectedRowMap[entry[this.props.id]],
        );
        const selectedRowIds = selectedRows.map(entry => entry[this.props.id]);
        this.props.onSelectedRowsChange(selectedRowIds, selectedRows);
        this.props.onRowCheckedStatusChange(id, checked);
      },
    );
  }

  generateTableSection(group: GroupInfo) {
    const self = this;

    const headerStyle: React.CSSProperties = {};
    const contentStyle: React.CSSProperties = {};
    const bodyStyle: React.CSSProperties = {};
    const rowStyle: React.CSSProperties = {};
    const paginationStyle: React.CSSProperties = {};

    /* Page number calculation */
    const pageCount =
      this.props.pageCount ||
      Math.ceil((group.data || []).length / self.props.pageSize);
    const currentPage = this.props.currentPage || group.currentPage;

    if (this.props.height) {
      headerStyle.paddingRight = '6px';
      paginationStyle.paddingRight = '6px';

      bodyStyle.height = `calc(100% - ${this.props.headerHeight})`;
      if (this.props.pagination) {
        contentStyle.height = `calc(100% - ${ROW_HEIGHT})`; // one row for pagination
      }
    }

    // calculate column width
    const originalWidthList = this.props.columns.map(column => {
      if (!column.width) {
        return DEFAULT_COLUMN_WIDTH;
      }
      if (column.width.endsWith('%')) {
        return DEFAULT_COLUMN_WIDTH;
      }
      return column.width;
    });
    const totalEstimatedWidth = _.sum(originalWidthList.map(x => parseInt(x)));
    const calculatedColumnWidthList = this.props.columns.map(column => {
      if (!column.width) {
        return DEFAULT_COLUMN_WIDTH;
      }
      if (column.width.endsWith('%')) {
        return `${Math.ceil(
          (totalEstimatedWidth * parseInt(column.width)) / 100,
        )}px`;
      }
      return column.width;
    });
    // end of calculate column width

    const sectionMinWidths = _.clone(calculatedColumnWidthList);
    if (this.props.showRowSelection) {
      sectionMinWidths.unshift(CHECKBOX_COLUMN_WIDTH);
    }
    if (sectionMinWidths.length) {
      rowStyle.minWidth = `calc(${sectionMinWidths.join(' + ')})`;
    }

    return (
      <div className="mio-table__section">
        <div
          className="mio-table__section__content"
          style={{ ...contentStyle }}
        >
          <div
            className="mio-table__header"
            style={{
              ...headerStyle,
              ...rowStyle,
              height: this.props.headerHeight,
            }}
          >
            <div
              className="mio-table__row mio-table__row--header mio-table__row--header"
              style={{
                height: this.props.headerHeight,
                minHeight: this.props.headerHeight,
              }}
            >
              {this.props.showRowSelection && (
                <div
                  key="mio-table__cell--checkbox"
                  className="mio-table__cell mio-table__cell--checkbox"
                />
              )}
              {_.map(this.props.columns, (column, columnIndex) => {
                const style: React.CSSProperties = {};
                if (typeof column.width !== 'undefined') {
                  style.flex = '0 0 auto';
                  style.width = calculatedColumnWidthList[columnIndex];
                }
                if (typeof column.align !== 'undefined') {
                  style.textAlign = column.align;
                }

                const canSort = column.sorter;
                const sortDirection =
                  group.sortColumn === column.key
                    ? group.sortDirection
                    : undefined;

                return (
                  <div
                    key={column.key}
                    className={c('mio-table__cell', {
                      'mio-table__cell--sort-header': canSort,
                    })}
                    style={style}
                    onClick={() => {
                      if (!canSort) {
                        return;
                      }

                      let newSortDirection: SortDirection = 'ASC';
                      let newsortColumn = column.key;
                      if (group.sortColumn === newsortColumn) {
                        if (group.sortDirection === 'ASC') {
                          newSortDirection = 'DESC';
                        }
                        if (group.sortDirection === 'DESC') {
                          newSortDirection = undefined;
                          newsortColumn = undefined;
                        }
                      }

                      const sortState: SortState = {
                        sortColumn: newsortColumn,
                        sortDirection: newSortDirection,
                      };
                      this.updateState(group.path, sortState);
                      this.props.updateSort(sortState);
                    }}
                  >
                    <div>
                      <span style={{ verticalAlign: 'middle' }}>
                        {column.title}
                      </span>
                      {canSort && <SortingIcon direction={sortDirection} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div
            className="mio-table__body"
            style={{ ...rowStyle, ...bodyStyle }}
          >
            {_.map(
              group.pagedData || group.sortedData || group.data,
              ({ entry }, index) => {
                const rowProps =
                  typeof this.props.rowProps === 'function'
                    ? this.props.rowProps(entry)
                    : this.props.rowProps;

                return (
                  <React.Fragment key={`${entry[self.props.id]}-row`}>
                    <this.props.rowElement
                      {...rowProps}
                      className={`${rowProps.className ||
                        ''} mio-table__row mio-table__row--body ${
                        index % 2
                          ? 'mio-table__row--even'
                          : 'mio-table__row--odd'
                      }`}
                      mio-table-row-id={entry[self.props.id]}
                    >
                      {this.props.showRowSelection && (
                        <div
                          key="mio-table__cell--checkbox"
                          className="mio-table__cell mio-table__cell--checkbox"
                          onClick={ev => {
                            ev.stopPropagation();
                          }}
                        >
                          <Checkbox
                            checked={
                              this.state.selectedRowMap[entry[this.props.id]]
                            }
                            onChange={value => {
                              this.onCheckboxChange(
                                entry[this.props.id],
                                value.target.checked,
                              );
                            }}
                          />
                        </div>
                      )}
                      {_.map(this.props.columns, (column, columnIndex) => {
                        const cellProps =
                          typeof column.cellProps === 'function'
                            ? column.cellProps(entry)
                            : column.cellProps || {};

                        let style: React.CSSProperties = {};
                        if (typeof column.width !== 'undefined') {
                          style.flex = '0 0 auto';
                          style.width = calculatedColumnWidthList[columnIndex];
                        }
                        if (typeof column.align !== 'undefined') {
                          style.textAlign = column.align;
                        }
                        style = { ...style, ...(cellProps.style || {}) };

                        return (
                          <div
                            key={column.key}
                            className="mio-table__cell"
                            {...cellProps}
                            style={style}
                            mio-table-column-id={column.key}
                          >
                            {getDataFromEntry(
                              column.accessor,
                              entry,
                              index,
                              group.data,
                            )}
                          </div>
                        );
                      })}
                    </this.props.rowElement>
                    {entry.isDetailVisible && (
                      <div
                        key={`${entry[self.props.id]}-row-detail`}
                        className="mio-table__row"
                      >
                        <div className="mio-table__cell">
                          {this.props.rowDetailRender(entry)}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              },
            )}
          </div>
        </div>
        {this.props.pagination && pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onSelectPage={page => {
              if (this.props.pagination === 'front-end') {
                this.updateState(group.path, { currentPage: page });
              }
              this.props.updateDataForPage(page);
            }}
            withSearch={this.props.paginationWithSearch}
          />
        )}
      </div>
    );
  }

  updateState(path: string[] = [], object: any) {
    const newHierarchicalData = { ...this.state.hierarchicalData };
    let currentObject: GroupInfo = newHierarchicalData;
    path.forEach(p => {
      const originalData = currentObject.data || [];
      let newCurrentObject;
      currentObject.data = [];
      originalData.forEach(d => {
        if (d.value !== p) {
          // @ts-ignore
          currentObject.data.push(d);
        } else {
          newCurrentObject = { ...d };
          // @ts-ignore
          currentObject.data.push(newCurrentObject);
        }
      });
      // @ts-ignore
      currentObject = newCurrentObject;
    });

    _.each(object, (value, field) => {
      // @ts-ignore
      currentObject[field] = value;
    });

    if ('sortColumn' in object) {
      if (object.sortColumn) {
        const sortFunc = _.get(
          _.find(
            this.props.columns,
            column => column.key === object.sortColumn,
          ),
          'sorter',
          () => 0,
        );
        currentObject.sortedData = _.sortBy(currentObject.data, data =>
          sortFunc(data.entry),
        );
        if (object.sortDirection === 'DESC') {
          currentObject.sortedData = _.reverse(currentObject.sortedData);
        }
      } else {
        currentObject.sortedData = undefined;
      }
    }

    if (
      this.props.pagination === 'front-end' &&
      ('sortColumn' in object || 'currentPage' in object)
    ) {
      // @ts-ignore
      currentObject.pagedData = (
        currentObject.sortedData || currentObject.data
      ).slice(
        // @ts-ignore
        this.props.pageSize * (currentObject.currentPage - 1),
        // @ts-ignore
        this.props.pageSize * currentObject.currentPage,
      );
    }

    this.setState({ hierarchicalData: newHierarchicalData });
  }

  generateGroupHeader(group: GroupInfo) {
    const expansionClickHandler = () => {
      this.updateState(group.path, { expanded: !group.expanded });
    };
    return (
      <div className="mio-table__row mio-table__row--group-item-header">
        <div
          className="mio-table__cell mio-table__cell--group-item-header-name"
          onClick={expansionClickHandler}
        >
          {group.name}
        </div>
        <div className="mio-table__cell mio-table__cell--expand-icon">
          <i
            className={`iconfont icon-down group-expand-icon ${
              group.expanded ? 'expanded' : 'collapsed'
            }`}
            onClick={expansionClickHandler}
          />
        </div>
      </div>
    );
  }

  generateGroup(group: GroupInfo) {
    if (group.itemType === 'entry') {
      return this.generateTableSection(group);
    }
    return (
      <div className="mio-table__group">
        {_.map(group.data, innerGroup => {
          return (
            <div className="mio-table__group-item" key={innerGroup.value}>
              <div className="mio-table__group-item-header">
                {this.generateGroupHeader(innerGroup)}
              </div>
              {innerGroup.expanded && (
                <div className="mio-table__group-item-content">
                  {this.generateGroup(innerGroup)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const tableStyle: React.CSSProperties = {};
    if (this.props.height) {
      tableStyle.height = this.props.height;
    }

    return (
      <div className="mio-table" style={tableStyle} id={this.props.tableId}>
        {this.generateGroup(this.state.hierarchicalData)}
      </div>
    );
  }
}

export default Table;
